import { useCallback, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
} from "@salestrak/ui";

import type { EntityViewRow } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useViews } from "#app/lib/views/view-context";
import { viewPath } from "#app/lib/views/view-routes";

/**
 * The confirm step for deleting a saved view, and the landing navigation that
 * follows it.
 *
 * Both places that can delete a view — the Customize view panel and a tab's
 * right-click menu — open this, so the copy and the "where does the viewer end
 * up" rule are written once. The view being deleted is NOT always the one
 * mounted: a right-click can act on any tab in the row.
 */

export type DeleteViewDialogProps = {
  view: EntityViewRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function DeleteViewDialog({
  view,
  open,
  onOpenChange,
}: DeleteViewDialogProps) {
  const { scope, views, activeView, deleteView, closePanel } = useViews();
  const { replace } = useAppNavigate();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      const ok = await deleteView(view.id);
      if (!ok) return;
      onOpenChange(false);
      closePanel();
      // Only the tab the viewer is standing on needs somewhere to go.
      if (activeView?.id !== view.id) return;
      // The deleted view is still in `views` (the cache update lands with the
      // response) — pick the landing view from what will remain.
      const remaining = views.filter((row) => row.id !== view.id);
      const target =
        remaining.find((row) => row.isDefault) ?? remaining[0] ?? null;
      if (target) replace(viewPath(scope, target));
    } finally {
      setDeleting(false);
    }
  }, [
    view,
    activeView,
    deleteView,
    views,
    scope,
    replace,
    closePanel,
    onOpenChange,
  ]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete &ldquo;{view.name}&rdquo;?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This view is shared with everyone in the workspace. Deleting it
            removes the tab and its customizations for all of them. The
            Projects it showed are not affected.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose
            render={<Button variant="outline" />}
            disabled={deleting}
          >
            Cancel
          </AlertDialogClose>
          <Button
            color="destructive"
            onClick={() => {
              void handleDelete();
            }}
            disabled={deleting}
            loading={deleting}
          >
            Delete view
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
